// #355: the simulator. A program measured before anyone follows it, on a
// hypothetical athlete: no history, no MAX, no profile. Same counting rules as
// everything else (a hard set is EFFORT_HARD_SET_RIR, a synergist earns the
// AP-6 credit, the band is RP's), so two readers of the same program get the
// same numbers. Outcomes are directions, never promises; above MRV is
// diminishing returns, not a loss (measurement-contract §8ter.3). Pure.
import type { Program } from "./schemas/program.js";
import { roundTypeAt } from "./programRoundTypes.js";
import type { Exercise } from "./schemas/exercise.js";
import type { MovementPattern } from "./schemas/muscle.js";
import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import { EFFORT_HARD_SET_RIR } from "./effortScale.js";
import { resolveFullCycleSessions } from "./programResolution.js";
import {
  cycleAverageByMuscle,
  hardSetsByMuscleByWeek,
  roundHardSetsOf,
  type RoundHardSets,
} from "./programPrescription.js";
import {
  RP_LANDMARK_SOURCE,
  landmarkZoneFor,
  weeklyLandmarkReadings,
  type VolumeLandmarkBand,
  type VolumeLandmarkZone,
} from "./volumeLandmarks.js";
import { catalogBySlug, resolveExerciseMusclesAbove } from "./volumeByMuscle.js";
import { HARD_SET_MUSCLE_CONTRIB_MIN } from "./weeklyTargetActual.js";
import { REST_FALLBACK_SEC, SUPERSET_REST_SEC } from "./restTimer.js";
import {
  SIMULATION_REP_TIME_SOURCE,
  SIMULATION_SECONDS_PER_REP,
  amrapSets,
  repsAndEffort,
  simulatedDays,
  type EffortDistribution,
  type RepsDistribution,
  type SimulatedAmrap,
  type SimulatedDay,
} from "./programSimulationSets.js";
import { staticRows, type StaticRow } from "./programSimulationRows.js";

/** Where a muscle is headed at the cycle's average, read off its band. A
 *  muscle with no hard set is not trained, whatever its MEV (some are 0). */
export type SimulationDirection =
  | "not-trained"
  | "unlikely-to-grow" // below MEV
  | "likely-to-grow" // MEV to MRV
  | "diminishing-returns" // above MRV
  | "unknown"; // no published band

export type WeekSets = { weekIdx: number; sets: number };

export type SimulatedMuscle = {
  muscle: CatalogMuscleSlug;
  /** Hard sets a week, the cycle's average, fractional credit included. */
  setsPerWeek: number;
  /** Of those, the sets with no RPE or RIR target, counted hard by assumption. */
  assumedSetsPerWeek: number;
  band?: VolumeLandmarkBand;
  zone?: VolumeLandmarkZone;
  direction: SimulationDirection;
  weeksBelowMev: WeekSets[];
  weeksAboveMrv: WeekSets[];
};

export type FrequencyRow<K> = { key: K; daysPerWeek: number };

export type SimulationAssumptions = {
  hardSetRir: number;
  landmarkSource: string;
  secondsPerRep: number;
  secondsPerRepSource: string;
  restFallbackSec: number;
  supersetRestSec: number;
};

export type ProgramSimulation = {
  cycleWeeks: number;
  /** The days of one round of the cycle: every session once. */
  sessionsPerRound: number;
  /** Program.sessionsPerWeek, as written. */
  sessionsPerWeek: number;
  /** The two differ: every «per week» number here is per round. */
  roundIsNotWeek: boolean;
  assumptions: SimulationAssumptions;
  muscles: SimulatedMuscle[];
  /** The whole cycle, raw counts with no credit. */
  hardSets: { total: number; assumed: number };
  frequency: {
    /** Days a week a muscle gets a hard set as a substantial mover. */
    muscles: FrequencyRow<CatalogMuscleSlug>[];
    /** Days a week a movement pattern gets a hard set. */
    patterns: FrequencyRow<MovementPattern>[];
  };
  reps: RepsDistribution;
  effort: EffortDistribution;
  staticRows: StaticRow[];
  days: SimulatedDay[];
  amrap: SimulatedAmrap[];
  /** Exercises the catalog does not know: they count for nothing. */
  unknownExercises: string[];
};

const directionOf = (sets: number, zone: VolumeLandmarkZone | undefined): SimulationDirection => {
  if (sets === 0) return "not-trained";
  if (zone === undefined) return "unknown";
  if (zone === "below-mev") return "unlikely-to-grow";
  return zone === "above-mrv" ? "diminishing-returns" : "likely-to-grow";
};

const simulatedMuscles = (
  rounds: ReadonlyArray<RoundHardSets>,
  cycleWeeks: number,
  catalog: ReadonlyArray<Exercise>,
  program: Program,
): SimulatedMuscle[] => {
  const byWeek = hardSetsByMuscleByWeek(rounds, cycleWeeks, catalog);
  const assumed = cycleAverageByMuscle(byWeek.assumed);
  return weeklyLandmarkReadings(cycleAverageByMuscle(byWeek.hard)).map((r) => {
    const weeks = byWeek.hard.map((w, weekIdx) => ({ weekIdx, sets: w[r.muscle] ?? 0 }));
    // A deload round is light on purpose: the cycle's average counts it, the
    // weeks below MEV or above MRV do not.
    const judged = weeks.filter((w) => roundTypeAt(program, w.weekIdx) !== "deload");
    const band = r.band;
    const zoneOf = (sets: number) => (band ? landmarkZoneFor(sets, band) : undefined);
    return {
      muscle: r.muscle,
      setsPerWeek: r.weeklySets,
      assumedSetsPerWeek: assumed[r.muscle] ?? 0,
      ...(band ? { band } : {}),
      ...(r.zone ? { zone: r.zone } : {}),
      direction: directionOf(r.weeklySets, r.zone),
      weeksBelowMev: judged.filter((w) => zoneOf(w.sets) === "below-mev"),
      weeksAboveMrv: judged.filter((w) => zoneOf(w.sets) === "above-mrv"),
    };
  });
};

/** Days a week each key gets at least one hard set, the cycle's average. */
const frequencyOf = <K extends string>(
  rounds: ReadonlyArray<RoundHardSets>,
  cycleWeeks: number,
  keysOf: (slug: string) => readonly K[],
): FrequencyRow<K>[] => {
  const days = new Map<K, number>();
  for (const day of rounds) {
    const hit = new Set<K>();
    for (const slug of Object.keys(day.byExercise)) {
      for (const k of keysOf(slug)) hit.add(k);
    }
    for (const k of hit) days.set(k, (days.get(k) ?? 0) + 1);
  }
  return [...days.entries()]
    .map(([key, n]) => ({ key, daysPerWeek: n / cycleWeeks }))
    .sort((a, b) => b.daysPerWeek - a.daysPerWeek || (a.key < b.key ? -1 : 1));
};

export const simulateProgram = (
  program: Program,
  catalog: ReadonlyArray<Exercise>,
): ProgramSimulation => {
  const cycleWeeks = program.cycleWeeks;
  const sessions = resolveFullCycleSessions(program);
  const rounds = roundHardSetsOf(sessions);
  const bySlug = catalogBySlug(catalog);
  const hardSets = { total: 0, assumed: 0 };
  for (const r of rounds) {
    for (const c of Object.values(r.byExercise)) {
      hardSets.total += c.hard;
      hardSets.assumed += c.assumed ?? 0;
    }
  }
  const unknown = new Set<string>();
  for (const s of sessions) {
    for (const e of s.entries) if (!bySlug.has(e.exerciseId)) unknown.add(e.exerciseId);
  }
  const exOf = (slug: string) => bySlug.get(slug);
  return {
    cycleWeeks,
    sessionsPerRound: program.sessions.length,
    sessionsPerWeek: program.sessionsPerWeek,
    roundIsNotWeek: program.sessions.length !== program.sessionsPerWeek,
    assumptions: {
      hardSetRir: EFFORT_HARD_SET_RIR,
      landmarkSource: RP_LANDMARK_SOURCE.id,
      secondsPerRep: SIMULATION_SECONDS_PER_REP,
      secondsPerRepSource: SIMULATION_REP_TIME_SOURCE,
      restFallbackSec: REST_FALLBACK_SEC,
      supersetRestSec: SUPERSET_REST_SEC,
    },
    muscles: simulatedMuscles(rounds, cycleWeeks, catalog, program),
    hardSets,
    frequency: {
      muscles: frequencyOf(rounds, cycleWeeks, (slug) => {
        const ex = exOf(slug);
        return ex ? resolveExerciseMusclesAbove(ex, HARD_SET_MUSCLE_CONTRIB_MIN) : [];
      }),
      patterns: frequencyOf(rounds, cycleWeeks, (slug) => {
        const p = exOf(slug)?.movementPattern;
        return p ? [p] : [];
      }),
    },
    ...repsAndEffort(sessions),
    staticRows: staticRows(program),
    days: simulatedDays(sessions, bySlug),
    amrap: amrapSets(sessions),
    unknownExercises: [...unknown].sort(),
  };
};
